import { SizeIcon } from '@radix-ui/react-icons';
import { Maximize, PanelRight, PictureInPicture, X } from 'lucide-react';
import { type ComponentType } from 'react';

import { type ChatPanelMode } from '@apps/web/lib/chat_ui_persistence';

// The chat panel's size glyphs, kept in one place so the rail in the panel
// header and the launcher that replaces the panel always say the same thing.
// Swapping the family is a change to this file alone.
//
// Linear's window pair is drawn here: a nested rounded square for the small
// window, and a squircle with a dash to expand it. Lucide has no match that
// survives 16px.

type ChatModeIcon = ComponentType<{ readonly className?: string }>;

export function ChatFloatingIcon({
    className,
}: {
    readonly className?: string;
}) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={className}
        >
            <rect x="5" y="5" width="14" height="14" rx="3.5" />
            <rect x="8.5" y="8.5" width="7" height="7" rx="2" />
        </svg>
    );
}

function ChatExpandFromWindowIcon({
    className,
}: {
    readonly className?: string;
}) {
    return (
        <SizeIcon
            aria-hidden="true"
            className={className}
            height={16}
            width={16}
        />
    );
}

export const CHAT_MODE_ICONS: Readonly<Record<ChatPanelMode, ChatModeIcon>> = {
    floating: ChatFloatingIcon,
    sidebar: PanelRight,
    fullscreen: Maximize,
};

export const CHAT_MODE_LABELS: Readonly<Record<ChatPanelMode, string>> = {
    floating: 'Float',
    sidebar: 'Dock to the side',
    fullscreen: 'Full screen',
};

// The header shows the way out of the current size, not every destination.
// Sidebar is home: float and full screen only offer dock; dock only offers float.
export function headerLayoutDestination(mode: ChatPanelMode): ChatPanelMode {
    switch (mode) {
        case 'floating':
        case 'fullscreen':
            return 'sidebar';
        case 'sidebar':
            return 'floating';
        default: {
            const exhaustive: never = mode;
            throw new Error(`Unhandled chat panel mode: ${exhaustive}`);
        }
    }
}

// Floating is a small window, so the way out is Radix Size (expand arrows),
// not the docked sidebar tile.
export function headerLayoutIcon(mode: ChatPanelMode): ChatModeIcon {
    switch (mode) {
        case 'floating':
            return ChatExpandFromWindowIcon;
        case 'fullscreen':
            return CHAT_MODE_ICONS.sidebar;
        case 'sidebar':
            return PictureInPicture;
        default: {
            const exhaustive: never = mode;
            throw new Error(`Unhandled chat panel mode: ${exhaustive}`);
        }
    }
}

// Hidden is not a size on that axis — it is the panel being away — so it keeps
// its own control outside the rail.
export const ChatHiddenIcon = X;
